import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import flatten from 'ramda/es/flatten'
import isNil from 'ramda/es/isNil'
import uniqBy from 'ramda/es/uniqBy'
import { loadPiniaSlice, persistPiniaSlice, persistPiniaSliceDebounced } from '../persist'

const questionQuery = (mainCategoryID, subCategoryID) => (q) =>
    q.mainCategoryID == mainCategoryID && q.subCategoryID == subCategoryID

const isCorrectPred = (q) =>
    q.anzahl_fragen <= 1
        ? q.answeredCorrectly
        : q.real_num_richtig_beantwortet > 0

const isWrongPred = (q) =>
    q.anzahl_fragen <= 1
        ? !q.answeredCorrectly
        : q.real_num_falsch_beantwortet > 0

const correctQuestionsOnly = (q) => !q.unedited && isCorrectPred(q)
const wrongQuestionsOnly = (q) => !q.unedited && isWrongPred(q)
const uneditedQuestionsOnly = (q) => q.unedited && !isCorrectPred(q)

export const useQuestionsStore = defineStore('questions', () => {
    const allQuestions = ref([])
    const currentQuestions = ref([])
    const unfilteredCurrentQuestions = ref([])
    const selectedQuestionIndex = ref(0)

    const showCorrectQuestions = ref(true)
    const showWrongQuestions = ref(true)
    const showUneditedQuestions = ref(true)
    const selectedSortOption = ref(undefined)

    const findByID = (id) =>
        flatten(allQuestions.value.map((x) => x.questions)).find((q) => q.id == id)

    const findInCurrentByID = (id) =>
        currentQuestions.value.find((q) => q.id == id)

    const forEachQuestionInAllPools = (id, apply) => {
        const inAll = findByID(id)
        if (inAll) apply(inAll)

        unfilteredCurrentQuestions.value.forEach((q) => {
            if (q.id == id) apply(q)
        })

        const inCurrent = findInCurrentByID(id)
        if (inCurrent) apply(inCurrent)
    }

    const patchQuestionInAllPools = (id, patch) => {
        forEachQuestionInAllPools(id, (q) => Object.assign(q, patch))
        persistDebounced()
    }

    const doQuestionsExistFor = (mainCategoryID, subCategoryID) =>
        !isNil(
            allQuestions.value.find(questionQuery(mainCategoryID, subCategoryID))
        )

    const filterCurrentQuestions = () => {
        const uniqQuestions = uniqBy((q) => q.id)

        const shouldInclude = (q) =>
            (showCorrectQuestions.value && correctQuestionsOnly(q)) ||
            (showWrongQuestions.value && wrongQuestionsOnly(q)) ||
            (showUneditedQuestions.value && uneditedQuestionsOnly(q))

        // Keep original sequence from payload (important for inserted question flow).
        currentQuestions.value = uniqQuestions(
            unfilteredCurrentQuestions.value.filter(shouldInclude)
        )
    }

    const prevQuestionIndex = () =>
        selectedQuestionIndex.value <= 0 ? 0 : selectedQuestionIndex.value - 1

    const allLoadedQuestions = computed(() => allQuestions.value)
    const currentQuestionsCount = computed(() => currentQuestions.value.length)
    const currentQuestion = computed(
        () => currentQuestions.value[selectedQuestionIndex.value]
    )
    const previousQuestion = computed(
        () => currentQuestions.value[prevQuestionIndex()]
    )

    const currentCorrectQuestionsCount = computed(
        () => currentQuestions.value.filter(correctQuestionsOnly).length
    )
    const currentWrongQuestionsCount = computed(
        () => currentQuestions.value.filter(wrongQuestionsOnly).length
    )
    const currentUneditedQuestionsCount = computed(
        () => currentQuestions.value.filter(uneditedQuestionsOnly).length
    )

    const absoluteCorrectQuestionsCount = computed(
        () => unfilteredCurrentQuestions.value.filter(correctQuestionsOnly).length
    )
    const absoluteWrongQuestionsCount = computed(
        () => unfilteredCurrentQuestions.value.filter(wrongQuestionsOnly).length
    )
    const absoluteUneditedQuestionsCount = computed(
        () => unfilteredCurrentQuestions.value.filter(uneditedQuestionsOnly).length
    )

    const resetAll = () => {
        allQuestions.value = []
        currentQuestions.value = []
        unfilteredCurrentQuestions.value = []
        selectedQuestionIndex.value = 0
        showCorrectQuestions.value = true
        showWrongQuestions.value = true
        showUneditedQuestions.value = true
        selectedSortOption.value = undefined
        persistToLocalStorage()
    }

    const incQuestionIndex = () => {
        if (selectedQuestionIndex.value < currentQuestions.value.length - 1) {
            selectedQuestionIndex.value++
            persistToLocalStorage()
        }
    }

    const decQuestionIndex = () => {
        if (selectedQuestionIndex.value > 0) {
            selectedQuestionIndex.value--
            persistToLocalStorage()
        }
    }

    const setQuestionIndexTo = (newIndex) => {
        if (newIndex >= 0 && newIndex < currentQuestions.value.length) {
            selectedQuestionIndex.value = newIndex
            persistToLocalStorage()
        }
    }

    const resetQuestionIndex = () => {
        selectedQuestionIndex.value = 0
        persistToLocalStorage()
    }

    const loadQuestionsFor = ({ mainCategoryID, subCategoryID }) => {
        if (doQuestionsExistFor(mainCategoryID, subCategoryID)) {
            unfilteredCurrentQuestions.value = allQuestions.value
                .find(questionQuery(mainCategoryID, subCategoryID))
                .questions.map((q) => {
                    if (isNil(q.mainCategoryID)) q.mainCategoryID = mainCategoryID
                    if (isNil(q.subCategoryID)) q.subCategoryID = subCategoryID
                    return q
                })
            filterCurrentQuestions()
            persistToLocalStorage()
        } else {
            console.log('No questions found!')
        }
    }

    const setActiveQuestions = (questions) => {
        currentQuestions.value = questions
        persistToLocalStorage()
    }

    const setCurrentQuestions = (questions) => {
        currentQuestions.value = questions
        unfilteredCurrentQuestions.value = questions
        persistToLocalStorage()
    }

    const addToAllQuestions = ({ mainCategoryID, subCategoryID, questions }) => {
        questions.forEach((q) => {
            q.modified_answers = false
        })
        allQuestions.value = allQuestions.value.filter(
            (entry) =>
                !(
                    Number(entry.mainCategoryID) === Number(mainCategoryID)
                    && Number(entry.subCategoryID) === Number(subCategoryID)
                )
        )
        allQuestions.value.push({ mainCategoryID, subCategoryID, questions })
        persistToLocalStorage()
    }

    const setShowCorrectQuestionsTo = (value) => {
        showCorrectQuestions.value = value
        filterCurrentQuestions()
        persistToLocalStorage()
    }

    const setShowWrongQuestionsTo = (value) => {
        showWrongQuestions.value = value
        filterCurrentQuestions()
        persistToLocalStorage()
    }

    const setShowUneditedQuestionsTo = (value) => {
        showUneditedQuestions.value = value
        filterCurrentQuestions()
        persistToLocalStorage()
    }

    const markQuestion = (id) => {
        patchQuestionInAllPools(id, { marked: true })
    }

    const unmarkQuestion = (id) => {
        patchQuestionInAllPools(id, { marked: false })
    }

    const setUneditedTo = (data) => {
        patchQuestionInAllPools(data.id, { unedited: data.value })
    }

    const setAnsweredCorrectlyTo = (data) => {
        patchQuestionInAllPools(data.id, { answeredCorrectly: data.value })
    }

    const setModifiedTo = (data) => {
        patchQuestionInAllPools(data.id, { modified_answers: data.value })
    }

    const setScoredPointsTo = (data) => {
        patchQuestionInAllPools(data.id, { scoredPoints: data.value })
    }

    const setRealNumOfWrongQuestions = (data) => {
        patchQuestionInAllPools(data.id, { real_num_falsch_beantwortet: data.num })
    }

    const setRealNumOfCorrectQuestions = (data) => {
        patchQuestionInAllPools(data.id, { real_num_richtig_beantwortet: data.num })
    }

    const setSelectedSortOption = (value) => {
        selectedSortOption.value = value
        persistToLocalStorage()
    }

    const persistPayload = () => ({
        selectedQuestionIndex: selectedQuestionIndex.value,
        showCorrectQuestions: showCorrectQuestions.value,
        showWrongQuestions: showWrongQuestions.value,
        showUneditedQuestions: showUneditedQuestions.value,
        selectedSortOption: selectedSortOption.value,
    })

    const persistToLocalStorage = () => {
        persistPiniaSlice('questionsStore', persistPayload())
    }

    const persistDebounced = () => {
        persistPiniaSliceDebounced('questionsStore', persistPayload())
    }

    const loadFromLocalStorage = () => {
        const qs = loadPiniaSlice('questionsStore')
        if (!qs) return
        if (typeof qs.selectedQuestionIndex === 'number') {
            selectedQuestionIndex.value = qs.selectedQuestionIndex
        }
        if (typeof qs.showCorrectQuestions === 'boolean') {
            showCorrectQuestions.value = qs.showCorrectQuestions
        }
        if (typeof qs.showWrongQuestions === 'boolean') {
            showWrongQuestions.value = qs.showWrongQuestions
        }
        if (typeof qs.showUneditedQuestions === 'boolean') {
            showUneditedQuestions.value = qs.showUneditedQuestions
        }
        if (qs.selectedSortOption !== undefined) {
            selectedSortOption.value = qs.selectedSortOption
        }
    }

    loadFromLocalStorage()

    return {
        allQuestions,
        currentQuestions,
        unfilteredCurrentQuestions,
        selectedQuestionIndex,
        showCorrectQuestions,
        showWrongQuestions,
        showUneditedQuestions,
        selectedSortOption,

        allLoadedQuestions,
        currentQuestionsCount,
        currentQuestion,
        previousQuestion,
        doQuestionsExistFor,
        currentCorrectQuestionsCount,
        currentWrongQuestionsCount,
        currentUneditedQuestionsCount,
        absoluteCorrectQuestionsCount,
        absoluteWrongQuestionsCount,
        absoluteUneditedQuestionsCount,

        resetAll,
        incQuestionIndex,
        decQuestionIndex,
        setQuestionIndexTo,
        resetQuestionIndex,
        loadQuestionsFor,
        setActiveQuestions,
        setCurrentQuestions,
        addToAllQuestions,
        setShowCorrectQuestionsTo,
        setShowWrongQuestionsTo,
        setShowUneditedQuestionsTo,
        markQuestion,
        unmarkQuestion,
        setUneditedTo,
        setAnsweredCorrectlyTo,
        setModifiedTo,
        setScoredPointsTo,
        setRealNumOfWrongQuestions,
        setRealNumOfCorrectQuestions,
        setSelectedSortOption,
    }
})
